/* oxlint-disable no-inner-declarations */
import * as rq from '@tanstack/react-query';

import { useDexFetchClient } from '@/api/looker/utils/use-dex-fetch-client';
import type { Top } from '@/data-explorer/entities/top';
import type { Unit } from '@/data-explorer/entities/unit';
import { useMe } from '@/hooks/use-me';

export namespace ListUnits {
  export const key = (input: Input) => [
    'data-explorer',
    'list-units',
    input.top?.id,
    input.top?.source,
  ];

  export type QueryParams = {
    retry?: number | boolean;
    keepPreviousData?: boolean;
  };

  export type Input = {
    top?: Top;
  };

  export type Output = {
    units: Unit[];
  };

  type FetchResult = Record<string, Record<string, Record<string, string>>>;

  function outputFromBody(data: FetchResult): Output {
    const units: Unit[] = [];

    for (const [key, innerObj] of Object.entries(data)) {
      for (const [innerKey, innerValue] of Object.entries(innerObj)) {
        for (const [subKey, id] of Object.entries(innerValue)) {
          units.push({
            id,
            label: subKey,
            source: key,
            level: innerKey,
          });
        }
      }
    }

    return { units };
  }

  export function useQuery(
    input: Input,
    { retry, keepPreviousData }: QueryParams = {},
  ) {
    const fetchClient = useDexFetchClient();
    const me = useMe();

    return rq.useQuery<Output>({
      retry,
      keepPreviousData,
      enabled: Boolean(input.top),
      queryKey: key(input),
      async queryFn({ signal }) {
        const data = await fetchClient<FetchResult>({
          path: `/v1/ids/${me.tenant}`,
          params: {
            'level-type': 'unit',
            'top-level-id': input.top?.id ?? '',
            'data-source': input.top?.source ?? '',
          },
          signal,
        });

        return outputFromBody(data);
      },
    });
  }
}
